import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar,
} from "recharts";
import { DollarSign, Database, Activity } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { getThemeAccent, getThemeSuccess } from "@/theme/tokens";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const TABS = [
  { key: "revenue",       label: "Revenue",       Icon: DollarSign },
  { key: "brokers",       label: "Broker Usage",  Icon: Database },
  { key: "user-activity", label: "User Activity", Icon: Activity },
];

const chartAxis = { stroke: "#5A7A9A", fontSize: 11 };
const chartGrid = { stroke: "#1E3A58", strokeDasharray: "3 3" };
const chartTooltip = {
  contentStyle: {
    background: "#0D1E36",
    border: "1px solid #1E3A58",
    borderRadius: 8,
    fontSize: 12,
    color: "#FFFFFF",
  },
  labelStyle: { color: "#A8B8D0" },
};

const RevenueTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/reports/revenue?months=12");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  const chartData = useMemo(() => (data?.months || []).map((m) => ({
    month: m.month,
    Revenue: (m.revenue_cents || 0) / 100,
    New: m.new_subs || 0,
    Cancels: m.cancels || 0,
  })), [data]);
  const accent = getThemeAccent();

  if (loading) return <Skeleton className="h-[400px]" />;

  return (
    <div className="space-y-5" data-testid="admin-report-revenue">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <JeaCard>
          <div className="jea-caption">Current MRR</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[28px] mt-2">
            {centsToUSD(data?.current_mrr_cents || 0)}
          </div>
        </JeaCard>
        <JeaCard>
          <div className="jea-caption">Current ARR</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[28px] mt-2">
            {centsToUSD(data?.current_arr_cents || 0)}
          </div>
        </JeaCard>
      </div>

      <JeaCard data-testid="admin-report-revenue-chart">
        <div className="jea-caption mb-3">MRR — Last 12 Months</div>
        <div className="w-full h-[280px]">
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="month" {...chartAxis} />
              <YAxis {...chartAxis} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <Tooltip {...chartTooltip} formatter={(v) => `$${Number(v).toLocaleString()}`} />
              <Line type="monotone" dataKey="Revenue" stroke={accent} strokeWidth={2}
                    dot={{ fill: accent, r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </JeaCard>

      <JeaCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default">
                <th className="jea-caption text-text-secondary p-4 text-left">Month</th>
                <th className="jea-caption text-text-secondary p-4 text-right">New Subs</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Cancels</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Net Change</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {(data?.months || []).map((m) => {
                const net = (m.new_subs || 0) - (m.cancels || 0);
                return (
                  <tr key={m.month} className="border-b border-border-default hover:bg-bg-tertiary" data-testid={`report-revenue-row-${m.month}`}>
                    <td className="p-4 jea-date">{m.month}</td>
                    <td className="p-4 text-right jea-mono text-success">{m.new_subs || 0}</td>
                    <td className="p-4 text-right jea-mono text-warning">{m.cancels || 0}</td>
                    <td className={cn("p-4 text-right jea-mono font-semibold",
                      net > 0 ? "text-success" : net < 0 ? "text-danger" : "text-text-secondary")}>
                      {net > 0 ? "+" : ""}{net}
                    </td>
                    <td className="p-4 text-right jea-mono text-accent-pink font-bold">
                      {centsToUSD(m.revenue_cents || 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </JeaCard>
    </div>
  );
};

const BrokersTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/reports/broker-usage?days=30");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  const accent = getThemeAccent();
  const chartData = (data?.brokers || []).map((b) => ({ name: b.name, Queries: b.queries }));

  if (loading) return <Skeleton className="h-[400px]" />;

  return (
    <div className="space-y-5" data-testid="admin-report-brokers">
      <JeaCard data-testid="admin-report-brokers-chart">
        <div className="jea-caption mb-3">Broker Query Volume — Last 30 Days</div>
        <div className="w-full h-[280px]">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ left: -10 }}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="name" {...chartAxis} interval={0} angle={-15} textAnchor="end" height={70} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} />
              <Bar dataKey="Queries" fill={accent} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </JeaCard>

      <JeaCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default">
                <th className="jea-caption text-text-secondary p-4 text-left">Broker</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Queries (30d)</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Revenue (30d)</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Avg / Query</th>
              </tr>
            </thead>
            <tbody>
              {(data?.brokers || []).map((b) => (
                <tr key={b.slug} className="border-b border-border-default hover:bg-bg-tertiary" data-testid={`report-broker-row-${b.slug}`}>
                  <td className="p-4 text-white font-semibold text-[13px]">{b.name}</td>
                  <td className="p-4 text-right jea-mono">{b.queries}</td>
                  <td className="p-4 text-right jea-mono text-accent-pink font-bold">{centsToUSD(b.revenue_cents)}</td>
                  <td className="p-4 text-right jea-mono text-text-secondary">{centsToUSD(b.avg_cost_cents || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </JeaCard>
    </div>
  );
};

const UserActivityTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/admin/reports/user-activity?days=30");
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  const accent = getThemeAccent();
  const chartData = (data?.daily_active || []).map((d) => ({ day: d.day, DAU: d.active_users }));

  if (loading) return <Skeleton className="h-[400px]" />;
  return (
    <div className="space-y-5" data-testid="admin-report-user-activity">
      <JeaCard data-testid="admin-report-dau-chart">
        <div className="jea-caption mb-3">Daily Active Users — Last 30 Days</div>
        <div className="w-full h-[280px]">
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="day" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} />
              <Line type="monotone" dataKey="DAU" stroke={accent} strokeWidth={2}
                    dot={{ fill: accent, r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </JeaCard>
      <JeaCard className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default">
                <th className="jea-caption text-text-secondary p-4 text-left">User</th>
                <th className="jea-caption text-text-secondary p-4 text-left">Type</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Queries YTD</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Spend YTD</th>
                <th className="jea-caption text-text-secondary p-4 text-left">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {(data?.top_users || []).map((u) => (
                <tr key={u.user_id} className="border-b border-border-default hover:bg-bg-tertiary" data-testid={`report-user-row-${u.user_id}`}>
                  <td className="p-4">
                    <div className="text-white font-semibold text-[13px]">{u.full_name}</div>
                    <div className="text-text-secondary text-[12px]">{u.email}</div>
                  </td>
                  <td className="p-4 uppercase tracking-[0.06em] font-semibold text-[11px] text-text-secondary">{u.account_type}</td>
                  <td className="p-4 text-right jea-mono">{u.queries_total}</td>
                  <td className="p-4 text-right jea-mono text-accent-pink font-bold">{centsToUSD(u.spend_cents || 0)}</td>
                  <td className="p-4 jea-date text-[13px]">{u.last_active_at ? relativeTime(u.last_active_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </JeaCard>
    </div>
  );
};

const AdminReportsPage = () => {
  const [tab, setTab] = useState("revenue");

  return (
    <div className="space-y-5" data-testid="admin-reports-page">
      <div className="space-y-1">
        <div className="jea-caption">Operational Insights</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Reports</h1>
      </div>

      <div className="flex items-center gap-1 border-b border-border-default overflow-x-auto" data-testid="admin-reports-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              "px-5 h-12 text-[13px] font-semibold border-b-2 -mb-px inline-flex items-center gap-2 transition-colors",
              tab === t.key
                ? "border-accent-pink text-accent-pink"
                : "border-transparent text-text-secondary hover:text-white",
            )}
            data-testid={`admin-reports-tab-${t.key}`}
          >
            <t.Icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <motion.div
        key={tab}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
      >
        {tab === "revenue" && <RevenueTab />}
        {tab === "brokers" && <BrokersTab />}
        {tab === "user-activity" && <UserActivityTab />}
      </motion.div>
    </div>
  );
};

export default AdminReportsPage;
